import React, { useState, useEffect, useRef } from 'react';
import { FaCalculator, FaChartLine, FaQuestionCircle } from 'react-icons/fa';
import { Link, useNavigate } from 'react-router-dom';
import { Chart, registerables } from 'chart.js';
import { Helmet } from 'react-helmet';

Chart.register(...registerables);

const TDSInterestCalculator = () => {
  const navigate = useNavigate();
  const [principal, setPrincipal] = useState('');
  const [rate, setRate] = useState('');
  const [time, setTime] = useState('');
  const [interest, setInterest] = useState(0);
  const [showResult, setShowResult] = useState(false);
  const [chartData, setChartData] = useState({});
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  const calculateInterest = () => {
    if (!principal || !rate || !time) {
      alert('Please fill in all fields');
      return;
    }

    const p = parseFloat(principal);
    const r = parseFloat(rate);
    const t = parseFloat(time);
    const calculatedInterest = (p * r * t) / 100;
    setInterest(calculatedInterest);
    setShowResult(true);

    // Generate chart data
    const yearlyData = [];
    for (let i = 1; i <= t; i++) {
      yearlyData.push((p * r * i) / 100);
    }
    setChartData({
      labels: Array.from({ length: t }, (_, i) => `Year ${i + 1}`),
      datasets: [
        {
          label: 'Interest Accumulation',
          data: yearlyData,
          fill: true,
          borderColor: '#3b82f6',
          tension: 0.1,
          backgroundColor: 'rgba(59, 130, 246, 0.1)'
        },
      ],
    });
  };

  useEffect(() => {
    if (chartData.labels && chartRef.current) {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }

      chartInstance.current = new Chart(chartRef.current, {
        type: 'line',
        data: chartData,
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            y: {
              beginAtZero: true,
              ticks: {
                callback: function(value) {
                  return '₹' + value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
                }
              }
            }
          },
          plugins: {
            legend: {
              position: 'top'
            },
            title: {
              display: true,
              text: 'Interest Growth Over Time'
            }
          }
        }
      });
    }
  }, [chartData]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 py-12 px-4 sm:px-6 lg:px-8">
      <Helmet>
        <title>TDS Interest Calculator</title>
        <meta name="description" content="Calculate TDS interest on your investments and visualize the growth over time using our simple interest calculator." />
        <meta property="og:title" content="TDS Interest Calculator" />
        <meta property="og:description" content="Calculate TDS interest on your investments and visualize the growth over time using our simple interest calculator." />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://your-domain.com/tds-calculator-preview.png" />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:title" content="TDS Interest Calculator" />
        <meta property="twitter:description" content="Calculate TDS interest on your investments and visualize the growth over time using our simple interest calculator." />
      </Helmet>
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 mb-4">
            <FaCalculator className="text-blue-600 text-3xl" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">TDS Interest Calculator</h1>
          <p className="text-gray-600">Calculate and visualize TDS interest on your investments</p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <label className="block text-sm font-medium text-gray-700">
                Principal Amount (₹)
              </label>
              <div className="relative">
                <FaCalculator className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="number"
                  value={principal}
                  onChange={(e) => setPrincipal(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter principal amount"
                />
              </div>
            </div>

            <div className="space-y-4">
              <label className="block text-sm font-medium text-gray-700">
                TDS Rate (%)
              </label>
              <input
                type="number"
                value={rate}
                onChange={(e) => setRate(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter TDS rate"
              />
            </div>

            <div className="space-y-4">
              <label className="block text-sm font-medium text-gray-700">
                Time Period (years)
              </label>
              <input
                type="number"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter time period"
              />
            </div>
          </div>

          <button
            onClick={calculateInterest}
            className="mt-6 w-full bg-gradient-to-r from-blue-600 to-purple-600 text-green-600 py-3 px-6 rounded-lg hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-300"
          >
            <span className="inline-flex items-center justify-center">
              <span className="mr-2">Calculate Interest</span>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </span>
          </button>
        </div>

        {showResult && (
          <div className="space-y-8">
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900">Interest Amount</h3>
                <p className="mt-2 text-2xl font-bold text-green-600">
                  ₹{interest.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                </p>
              </div>

              <div className="mt-8">
                <canvas ref={chartRef} className="w-full h-64" />
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Features</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="p-4 bg-blue-50 rounded-lg">
                  <FaCalculator className="text-blue-600 text-2xl mb-2" />
                  <h3 className="text-lg font-semibold text-gray-900">Simple Interest Calculation</h3>
                  <p className="text-gray-600">Calculate TDS interest using the simple interest formula</p>
                </div>
                <div className="p-4 bg-blue-50 rounded-lg">
                  <FaChartLine className="text-blue-600 text-2xl mb-2" />
                  <h3 className="text-lg font-semibold text-gray-900">Visual Representation</h3>
                  <p className="text-gray-600">View interest growth over time with an interactive graph</p>
                </div>
                <div className="p-4 bg-blue-50 rounded-lg">
                  <FaQuestionCircle className="text-blue-600 text-2xl mb-2" />
                  <h3 className="text-lg font-semibold text-gray-900">Real-time Updates</h3>
                  <p className="text-gray-600">Instant results as you enter your values</p>
                </div>
                <div className="p-4 bg-blue-50 rounded-lg">
                  <FaQuestionCircle className="text-blue-600 text-2xl mb-2" />
                  <h3 className="text-lg font-semibold text-gray-900">Yearly Breakdown</h3>
                  <p className="text-gray-600">See how interest accumulates each year</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Benefits</h2>
              <div className="space-y-4">
                <div className="flex items-start">
                  <FaQuestionCircle className="text-blue-600 text-xl mr-3 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Financial Planning</h3>
                    <p className="text-gray-600">Plan your investments better by understanding TDS implications</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <FaQuestionCircle className="text-blue-600 text-xl mr-3 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Tax Compliance</h3>
                    <p className="text-gray-600">Stay compliant with tax regulations by calculating TDS accurately</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <FaQuestionCircle className="text-blue-600 text-xl mr-3 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Investment Analysis</h3>
                    <p className="text-gray-600">Analyze the impact of TDS on your investment returns</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">How to Use</h2>
              <div className="space-y-4">
                <div className="flex items-start">
                  <FaQuestionCircle className="text-blue-600 text-xl mr-3 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Step 1: Enter Principal Amount</h3>
                    <p className="text-gray-600">Input your principal amount in the first field</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <FaQuestionCircle className="text-blue-600 text-xl mr-3 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Step 2: Input TDS Rate</h3>
                    <p className="text-gray-600">Enter the TDS rate in percentage</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <FaQuestionCircle className="text-blue-600 text-xl mr-3 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Step 3: Specify Time Period</h3>
                    <p className="text-gray-600">Specify the time period in years</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <FaQuestionCircle className="text-blue-600 text-xl mr-3 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Step 4: Calculate Interest</h3>
                    <p className="text-gray-600">Click "Calculate Interest" to see results</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">FAQs</h2>
              <div className="space-y-4">
                <div className="border-l-4 border-blue-600 pl-4">
                  <h3 className="font-semibold text-gray-900">What is TDS?</h3>
                  <p className="text-gray-600">TDS stands for Tax Deducted at Source. It's a tax collection mechanism where the person responsible for making specified payments to any person deducts tax at the appropriate rate before making the payment.</p>
                </div>
                <div className="border-l-4 border-blue-600 pl-4">
                  <h3 className="font-semibold text-gray-900">How is TDS interest calculated?</h3>
                  <p className="text-gray-600">TDS interest is calculated using the simple interest formula: (Principal × Rate × Time) / 100</p>
                </div>
                <div className="border-l-4 border-blue-600 pl-4">
                  <h3 className="font-semibold text-gray-900">Why is TDS important?</h3>
                  <p className="text-gray-600">TDS helps in automatic tax collection at the source of income and ensures regular flow of revenue to the government.</p>
                </div>
                <div className="border-l-4 border-blue-600 pl-4">
                  <h3 className="font-semibold text-gray-900">Can TDS be refunded?</h3>
                  <p className="text-gray-600">Yes, if the TDS deducted is more than your actual tax liability, you can claim a refund during income tax filing.</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TDSInterestCalculator;
